import { useState, useCallback, useEffect } from 'react';
import { listWorkspace, Workspace } from '@goldcast/api/backend';
import { useAppContext } from '@/context/AppContext/AppContext';

interface UseWorkspaceReturn {
  workspaces: Workspace[];
  isLoading: boolean;
  error: string | null;
  fetchWorkspaces: () => Promise<void>;
  getWorkspaceById: (id: string) => Workspace | undefined;
  getWorkspaceOptions: () => Array<{ name: string }>;
}

export default function useWorkspace(): UseWorkspaceReturn {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { adminAppStore, logger } = useAppContext();

  const fetchWorkspaces = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const currentOrg = adminAppStore?.currentOrg;
      if (!currentOrg?.id) {
        throw new Error('No organization found');
      }

      const workspaceData = await listWorkspace({
        queryParams: {
          organization: currentOrg.id,
          show: 'mine',
          search: '',
          ordering: 'name'
        }
      });

      // Handle both array and paginated response
      const workspaceList = Array.isArray(workspaceData) ? workspaceData : workspaceData.results || [];

      setWorkspaces(workspaceList);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch workspaces';
      setError(errorMessage);
      logger?.error('Error fetching workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  }, [adminAppStore?.currentOrg, logger]);

  const getWorkspaceById = useCallback((id: string) => workspaces.find(workspace => workspace.id === id), [workspaces]);

  const getWorkspaceOptions = useCallback(() => {
    return workspaces.map(workspace => ({
      name: workspace.name
    }));
  }, [workspaces]);

  // Fetch workspaces on mount
  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  return {
    workspaces,
    isLoading,
    error,
    fetchWorkspaces,
    getWorkspaceById,
    getWorkspaceOptions
  };
}
